import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiKey,
  CreatedApiKey,
  ApiScope,
  VALID_SCOPES,
  SCOPE_DESCRIPTIONS,
  PlatformApiError,
  listApiKeys,
  createApiKey,
  revokeApiKey,
} from '../services/platformApi';

const DEFAULT_RATE_LIMIT = 60;

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

/** A key is live until `revoked_at` is set; the backend never deletes rows. */
const isActive = (key: ApiKey): boolean => key.revoked_at === null;

function CopyButton({
  value,
  selectTargetRef,
  label = 'Copy',
}: {
  value: string;
  selectTargetRef?: React.RefObject<HTMLElement>;
  label?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'select'>('idle');
  // Timer id lives in a ref so the cleanup can clear it without the timeout
  // itself being part of render state.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const flash = (next: 'copied' | 'select') => {
    setState(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState('idle'), 2500);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      flash('copied');
    } catch {
      // Clipboard blocked (insecure origin, or permission denied). Select the
      // secret where it already is so the native copy shortcut works — a
      // prompt() dialog would just be a second place to copy it out of, and
      // it puts the secret somewhere the user did not ask for it.
      const node = selectTargetRef?.current;
      if (node && window.getSelection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      flash('select');
    }
  };

  return (
    <button
      onClick={copy}
      className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
    >
      {state === 'copied' ? 'Copied' : state === 'select' ? 'Press Ctrl/⌘ C' : label}
    </button>
  );
}

export default function ApiKeysPage() {
  const navigate = useNavigate();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  // Held as a string so an empty field stays empty while the user retypes,
  // instead of snapping back to the default mid-keystroke.
  const [rateLimit, setRateLimit] = useState<string>(String(DEFAULT_RATE_LIMIT));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  /** The one and only time the plaintext secret exists client-side. */
  const [revealedKey, setRevealedKey] = useState<CreatedApiKey | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  // Read inside `load` without making it a dependency — the skill's
  // `stateRef.current = state` pattern. Adding `revealedKey` to `load`'s deps
  // would rebuild the callback and re-fire the mount effect.
  const revealedKeyRef = useRef<CreatedApiKey | null>(null);
  revealedKeyRef.current = revealedKey;

  /** A secret element the clipboard fallback can select in place. */
  const secretRef = useRef<HTMLElement>(null);

  const parsedRateLimit = Number.parseInt(rateLimit, 10);
  const rateLimitValid =
    Number.isInteger(parsedRateLimit) && parsedRateLimit >= 1 && parsedRateLimit <= 6000;

  // 403 counts as an auth failure here: an unauthenticated GET /api/v1/keys
  // answers 403 (HTTPBearer with no credentials), and a suspended account
  // answers 403 from _enforce_account_status. Neither is retryable in place.
  const handleAuthError = useCallback(
    (err: unknown): boolean => {
      if (err instanceof PlatformApiError && (err.status === 401 || err.status === 403)) {
        navigate('/login');
        return true;
      }
      return false;
    },
    [navigate]
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const keys = await listApiKeys(signal);
      if (signal?.aborted) return;
      setApiKeys(keys);
      setError(null);

      // If the key whose secret is on screen has since been revoked, that
      // panel is inviting the user to save a credential that no longer works.
      const revealed = revealedKeyRef.current;
      if (revealed && keys.some((k) => k.id === revealed.id && k.revoked_at !== null)) {
        setRevealedKey(null);
      }
    } catch (err) {
      if (signal?.aborted) return;
      if (handleAuthError(err)) return;
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!localStorage.getItem('authToken')) {
      navigate('/login');
      return;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, navigate]);

  const toggleScope = (scope: ApiScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const resetCreateForm = () => {
    setNewKeyName('');
    setSelectedScopes([]);
    setRateLimit(String(DEFAULT_RATE_LIMIT));
    setCreateError(null);
  };

  const handleCreate = async () => {
    if (!newKeyName.trim() || selectedScopes.length === 0 || !rateLimitValid || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createApiKey(newKeyName.trim(), selectedScopes, parsedRateLimit);
      setShowCreateModal(false);
      resetCreateForm();
      setRevealedKey(created);
      await load();
    } catch (err) {
      if (handleAuthError(err)) return;
      setCreateError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    const confirmed = window.confirm(
      `Revoke "${key.name}" (${key.key_prefix}…)?\n\n` +
        'Any client using this key stops working immediately. This cannot be undone.'
    );
    if (!confirmed) return;

    setRevokingId(key.id);
    try {
      await revokeApiKey(key.id);
      // Clear eagerly too: `load` catches the case where it was revoked
      // elsewhere, this catches the one the user just revoked here.
      if (revealedKeyRef.current?.id === key.id) setRevealedKey(null);
      await load();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">API Keys</h1>
              <p className="text-gray-400">
                Scoped keys for the PropAlgos CLI, SDK, and agents. Keys carry only the
                scopes you grant and are rate limited per minute.
              </p>
            </div>
            <button
              onClick={() => {
                resetCreateForm();
                setShowCreateModal(true);
              }}
              className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create API Key
            </button>
          </div>

          {/* Freshly created secret — shown once, never retrievable again */}
          {revealedKey && (
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">
                    Copy your key now — this is the only time it is shown
                  </h3>
                  <p className="text-amber-300/90 text-sm">
                    PropAlgos stores only a SHA-256 hash of this secret. If you lose it,
                    revoke the key and create a new one.
                  </p>
                </div>
                <button
                  onClick={() => setRevealedKey(null)}
                  className="text-gray-400 hover:text-white transition-colors ml-4"
                  title="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-3">
                <code
                  ref={secretRef}
                  className="flex-1 bg-black/50 border border-amber-800/40 text-amber-100 px-4 py-3 rounded-lg text-sm font-mono break-all"
                >
                  {revealedKey.secret}
                </code>
                <CopyButton value={revealedKey.secret} selectTargetRef={secretRef} />
              </div>
              <p className="text-gray-400 text-xs mt-3">
                Store it in your OS keyring:{' '}
                <code className="bg-gray-800 px-2 py-0.5 rounded text-gray-300">
                  propalgos keys use {revealedKey.secret.slice(0, 7)}…
                </code>{' '}
                or export{' '}
                <code className="bg-gray-800 px-2 py-0.5 rounded text-gray-300">PROPALGOS_API_KEY</code>.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 mb-8 flex items-center justify-between">
              <span className="text-red-300 text-sm">{error}</span>
              <button
                onClick={() => load()}
                className="px-3 py-1.5 bg-red-800/50 hover:bg-red-800 text-white rounded-lg text-sm transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* API Keys List */}
          <div className="bg-gray-900 rounded-xl border border-gray-800">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Your API Keys</h2>
              <span className="text-sm text-gray-500">
                {apiKeys.filter(isActive).length} active · {apiKeys.length} total
              </span>
            </div>

            <div className="p-6">
              {apiKeys.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">No API keys yet</h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto">
                    Create a key to drive GPU sweeps and signal feeds from the CLI, the
                    Python SDK, or an agent.
                  </p>
                  <button
                    onClick={() => {
                      resetCreateForm();
                      setShowCreateModal(true);
                    }}
                    className="bg-white text-black px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                  >
                    Create API Key
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {apiKeys.map((apiKey) => {
                    const active = isActive(apiKey);
                    return (
                      <div
                        key={apiKey.id}
                        className={`rounded-lg p-6 border ${
                          active
                            ? 'bg-gray-800/50 border-gray-700/50'
                            : 'bg-gray-800/20 border-gray-800 opacity-70'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center mb-2">
                              <h3 className="text-lg font-semibold text-white mr-3 truncate">
                                {apiKey.name}
                              </h3>
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  active
                                    ? 'bg-green-500/20 text-green-500'
                                    : 'bg-red-500/20 text-red-500'
                                }`}
                              >
                                {active ? 'active' : 'revoked'}
                              </span>
                            </div>

                            <div className="space-y-2 text-sm text-gray-400">
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                <span>
                                  Key:{' '}
                                  <code className="bg-gray-700 px-2 py-1 rounded text-gray-300 font-mono">
                                    {apiKey.key_prefix}…
                                  </code>
                                </span>
                                <span>id {apiKey.id}</span>
                                <span>Created: {formatDate(apiKey.created_at)}</span>
                                <span>Last used: {formatDate(apiKey.last_used_at)}</span>
                                {!active && <span>Revoked: {formatDate(apiKey.revoked_at)}</span>}
                                <span>{apiKey.rate_limit_per_min} req/min</span>
                              </div>

                              <div className="flex items-start">
                                <span className="mr-2 flex-shrink-0">Scopes:</span>
                                <div className="flex flex-wrap gap-1">
                                  {apiKey.scopes.length === 0 ? (
                                    <span className="text-gray-500">none</span>
                                  ) : (
                                    apiKey.scopes.map((scope) => (
                                      <span
                                        key={scope}
                                        className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs font-mono"
                                      >
                                        {scope}
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            {active && (
                              <button
                                onClick={() => handleRevoke(apiKey)}
                                disabled={revokingId === apiKey.id}
                                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {revokingId === apiKey.id ? 'Revoking…' : 'Revoke'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-8 max-w-lg w-full border border-gray-800 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Create API Key</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Key name</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  maxLength={100}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., sweep-runner"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Scopes</label>
                <div className="space-y-2">
                  {VALID_SCOPES.map((scope) => (
                    <label
                      key={scope}
                      className="flex items-start space-x-3 cursor-pointer bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 hover:border-gray-600 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(scope)}
                        onChange={() => toggleScope(scope)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white font-mono">{scope}</div>
                        <div className="text-xs text-gray-400">{SCOPE_DESCRIPTIONS[scope]}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rate limit (requests per minute)
                </label>
                <input
                  type="number"
                  min={1}
                  max={6000}
                  value={rateLimit}
                  onChange={(e) => setRateLimit(e.target.value)}
                  onBlur={() => {
                    // Clamp on commit, not on keystroke, so a half-typed value
                    // is never rewritten under the cursor.
                    if (rateLimit.trim() === '') return;
                    if (!Number.isInteger(parsedRateLimit)) return;
                    const clamped = Math.min(6000, Math.max(1, parsedRateLimit));
                    if (clamped !== parsedRateLimit) setRateLimit(String(clamped));
                  }}
                  className={`w-full bg-gray-800 border text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    rateLimit.trim() !== '' && !rateLimitValid
                      ? 'border-red-700'
                      : 'border-gray-700'
                  }`}
                />
                <p
                  className={`text-xs mt-1 ${
                    rateLimit.trim() !== '' && !rateLimitValid ? 'text-red-400' : 'text-gray-500'
                  }`}
                >
                  {rateLimit.trim() !== '' && !rateLimitValid
                    ? 'Must be a whole number between 1 and 6000.'
                    : 'Between 1 and 6000. Default is 60.'}
                </p>
              </div>

              {createError && (
                <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3">
                  <span className="text-red-300 text-sm">{createError}</span>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={
                    !newKeyName.trim() || selectedScopes.length === 0 || !rateLimitValid || creating
                  }
                  className="px-6 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating…' : 'Create Key'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
